import {
  FC,
  FormEvent,
  FormEventHandler,
  Fragment,
  MouseEventHandler,
} from "react";
import cx from "classnames";
import { Transition } from "@headlessui/react";
import AddressStateSelect from "@common/components/AddressStateSelect";
import DatesInput from "@common/components/DatesInput";
import EmploymentStatusSelect from "@common/components/EmploymentStatusSelect";
import FinraAssociationSelect from "@common/components/FinraAssociationSelect";
import Input from "@common/components/Input";
import PhoneInput from "@common/components/PhoneInput";
import { ClientIntakeData } from "@modules/client/types";
import { Controller } from "react-hook-form";
import { CgSpinner } from "react-icons/cg";
import { useClientIntakeUpdate } from "@modules/client/mutations";
import InputLabel from "@common/components/InputLabel";
import SsnInput from "@common/components/SsnInput";
import React from "react";
import { serializeClient } from "@modules/client/utils.frontend";
import { HiArrowLeft } from "react-icons/hi";
import LoadingIndicator from "@common/components/LoadingIndicator";
import { useIntakeForm } from "@modules/tasks/hooks";
import { themeTextColors } from "@common/constants";
import { Button } from "@earned/core";

interface IntakeFormProps {
  currentPage: number;
  setCurrentPage: (page: number) => void;
  onClose: () => void;
}

const IntakeForm: FC<IntakeFormProps> = ({
  currentPage,
  setCurrentPage,
  onClose,
}) => {
  const {
    hasCoClient,
    setHasCoClient,
    serverError,
    setServerError,
    register,
    handleSubmit,
    control,
    isSubmitting,
    errors,
    isLoading,
    intakeFormValues,
    isSubmitDisabled,
    showPage1,
    showPage2,
    onAddCoClient,
  } = useIntakeForm(currentPage, setCurrentPage);

  const { mutateAsync: submitIntakeForm } = useClientIntakeUpdate();

  const onSubmit:
    | MouseEventHandler<HTMLButtonElement>
    | FormEventHandler<HTMLFormElement> = (event: FormEvent | MouseEvent) => {
    event.preventDefault();
    handleSubmit(async (data) => {
      setServerError(false);
      try {
        const payload: {
          client: ClientIntakeData;
          coClient?: ClientIntakeData;
        } = {
          client: serializeClient(data.client as ClientIntakeData),
        };
        if (hasCoClient && data.coClient) {
          payload.coClient = serializeClient(data.coClient);
        }
        await submitIntakeForm(payload);
        onClose();
      } catch (error) {
        console.log("Submit Error: ", error);
        setServerError(true);
      }
    })(event as FormEvent);
  };

  return (
    <form onSubmit={onSubmit as FormEventHandler}>
      <div className="flex flex-col overflow-y-scroll px-10 scrollbar-none">
        <div className="flex flex-row pb-10 pt-6 text-label-xs text-purple-2-900">
          <Transition
            appear={true}
            as={Fragment}
            show={isLoading}
            enter="transition-all duration-500 ease-out"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-all duration-500 ease-in"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              className={`relative flex h-[780px] flex-1 flex-row items-center justify-center lg:h-[380px]`}
            >
              <LoadingIndicator isVisible={true} className="absolute" />
            </div>
          </Transition>
          <Transition
            appear={true}
            unmount={false}
            as={Fragment}
            show={showPage1 && !isLoading}
            enter="transition-all duration-100 transform ease-in-out"
            enterFrom="opacity-0"
            enterTo="opacity-100 translate-x-0"
            leave="transition-all duration-300 ease-in-out transform"
            leaveFrom="opacity-100 translate-x-0"
            leaveTo="opacity-0 -translate-x-full"
          >
            <div
              key="client"
              className={cx(
                showPage1 ? "flex" : "hidden",
                "grid grid-cols-1 flex-col lg:grid-cols-2 lg:gap-10"
              )}
            >
              <div className="">
                <div>
                  <div className="grid grid-cols-6 gap-1">
                    <div className="col-span-4">
                      <Input
                        required
                        label={"Primary Residence Address"}
                        {...register("client.ADDRESS_STREET")}
                        error={errors.client?.ADDRESS_STREET}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        label={"Unit Number"}
                        {...register("client.ADDRESS_STREET_2")}
                        error={errors.client?.ADDRESS_STREET_2}
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <div className="grid grid-cols-7 gap-1">
                    <div className="col-span-3">
                      <Input
                        required
                        label={"City"}
                        {...register("client.ADDRESS_CITY")}
                        error={errors.client?.ADDRESS_CITY}
                      />
                    </div>
                    <div className="col-span-2">
                      <Controller
                        name="client.ADDRESS_STATE"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <AddressStateSelect
                            label="State"
                            required
                            {...field}
                            error={errors.client?.ADDRESS_STATE}
                            onChange={(value: any) =>
                              field.onChange(value.target.value)
                            }
                          />
                        )}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        required
                        maxLength={5}
                        label={"Zip Code"}
                        {...register("client.ADDRESS_ZIP")}
                        error={errors.client?.ADDRESS_ZIP}
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <div className="grid grid-cols-6 gap-1">
                    <div className="col-span-4">
                      <Input
                        label={"Mailing Address (if different)"}
                        {...register("client.MAILING_ADDRESS_STREET")}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        label={"Unit Number"}
                        {...register("client.MAILING_ADDRESS_STREET_2")}
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <div className="grid grid-cols-7 gap-1">
                    <div className="col-span-3">
                      <Input
                        label={"City"}
                        {...register("client.MAILING_ADDRESS_CITY")}
                      />
                    </div>
                    <div className="col-span-2">
                      <Controller
                        name="client.MAILING_ADDRESS_STATE"
                        control={control}
                        render={({ field }) => (
                          <AddressStateSelect
                            {...field}
                            label="State"
                            onChange={(value: any) =>
                              field.onChange(value.target.value)
                            }
                          />
                        )}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        maxLength={5}
                        label={"Zip Code"}
                        {...register("client.MAILING_ADDRESS_ZIP")}
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <div className="grid grid-cols-2 gap-1">
                    <div>
                      <SsnInput
                        defaultValue={intakeFormValues?.client?.SSN}
                        required
                        label={"Social Security Number"}
                        {...register("client.SSN")}
                        error={errors.client?.SSN}
                      />
                    </div>
                    <div>
                      <DatesInput
                        defaultValue={intakeFormValues?.client?.DOB}
                        required
                        label={"Date of Birth"}
                        {...register("client.DOB")}
                        error={errors.client?.DOB}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 lg:mt-0">
                <div>
                  <Controller
                    name="client.EMPLOYMENT_STATUS"
                    control={control}
                    render={({ field }) => (
                      <EmploymentStatusSelect
                        {...field}
                        label="Employment Status"
                        onChange={(value: any) => field.onChange(value)}
                      />
                    )}
                  />
                </div>
                <div>
                  <Input
                    label={"Employer Name"}
                    {...register("client.EMPLOYER_NAME")}
                  />
                </div>
                <div>
                  <Input
                    label={"Occupation"}
                    {...register("client.OCCUPATION")}
                  />
                </div>
                <div className="grid grid-cols-6 gap-1">
                  <div className="col-span-2">
                    <Controller
                      name="client.DRIVER_LICENSE_STATE"
                      rules={{ required: true }}
                      control={control}
                      render={({ field }) => (
                        <AddressStateSelect
                          {...field}
                          error={errors.client?.DRIVER_LICENSE_STATE}
                          label="Driver’s License State"
                          required={true}
                          onChange={(value: any) =>
                            field.onChange(value.target.value)
                          }
                        />
                      )}
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      required
                      label={"License Number"}
                      {...register("client.DRIVER_LICENSE_NUMBER")}
                      error={errors.client?.DRIVER_LICENSE_NUMBER}
                    />
                  </div>
                  <div className="col-span-2">
                    <DatesInput
                      required
                      defaultValue={
                        intakeFormValues?.client?.DRIVER_LICENSE_EXP
                      }
                      label={"Expiration Date"}
                      {...register("client.DRIVER_LICENSE_EXP")}
                      error={errors.client?.DRIVER_LICENSE_EXP}
                    />
                  </div>
                </div>
                <div>
                  <Controller
                    name="client.HAS_FINRA_ASSOCIATION"
                    rules={{ required: true }}
                    control={control}
                    render={({ field }) => (
                      <FinraAssociationSelect
                        label="FINRA Association"
                        {...field}
                        error={errors.client?.HAS_FINRA_ASSOCIATION}
                        onChange={(value: boolean) => field.onChange(value)}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </Transition>
          <Transition
            as={Fragment}
            appear={true}
            unmount={false}
            show={showPage2 && !isLoading}
            enter="transition-all duration-100 transform ease-in-out"
            enterFrom="opacity-0 translate-x-full"
            enterTo="opacity-100 translate-x-0"
            leave="transition-all duration-300 ease-in-out transform"
            leaveFrom="opacity-100 translate-x-0"
            leaveTo="opacity-0 translate-x-full"
          >
            <div
              key="co-client"
              className={cx(
                showPage2 ? "flex" : "hidden",
                "grid grid-cols-1 flex-col lg:grid-cols-2 lg:gap-10"
              )}
            >
              <div className="">
                <div>
                  <div className="grid grid-cols-4 gap-1">
                    <div className="col-span-2">
                      <Input
                        required
                        label={"Legal First Name"}
                        error={errors.coClient?.FIRST_NAME}
                        {...register("coClient.FIRST_NAME")}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        required
                        label={"Legal Last Name"}
                        {...register("coClient.LAST_NAME")}
                        error={errors.coClient?.LAST_NAME}
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <PhoneInput
                    required
                    defaultValue={intakeFormValues?.coClient?.PHONE_NUMBER}
                    label={"Phone Number"}
                    {...register("coClient.PHONE_NUMBER")}
                    error={errors.coClient?.PHONE_NUMBER}
                  />
                </div>
                <div>
                  <Input
                    required
                    label={"Email Address"}
                    {...register("coClient.EMAIL")}
                    error={errors.coClient?.EMAIL}
                  />
                </div>
                <div>
                  <div className="grid grid-cols-2 gap-1">
                    <div>
                      <SsnInput
                        required
                        defaultValue={intakeFormValues?.coClient?.SSN}
                        label={"Social Security Number"}
                        {...register("coClient.SSN")}
                        error={errors.coClient?.SSN}
                      />
                    </div>
                    <div>
                      <DatesInput
                        required
                        defaultValue={intakeFormValues?.coClient?.DOB}
                        label={"Date of Birth"}
                        {...register("coClient.DOB")}
                        error={errors.coClient?.DOB}
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <div className="grid grid-cols-6 gap-1">
                    <div className="col-span-2">
                      <Controller
                        name="coClient.DRIVER_LICENSE_STATE"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <AddressStateSelect
                            {...field}
                            error={errors.coClient?.DRIVER_LICENSE_STATE}
                            label="Driver's License State"
                            required={true}
                            onChange={(value: any) =>
                              field.onChange(value.target.value)
                            }
                          />
                        )}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        required
                        label={"License Number"}
                        {...register("coClient.DRIVER_LICENSE_NUMBER")}
                        error={errors.coClient?.DRIVER_LICENSE_NUMBER}
                      />
                    </div>
                    <div className="col-span-2">
                      <DatesInput
                        required
                        defaultValue={
                          intakeFormValues?.coClient?.DRIVER_LICENSE_EXP
                        }
                        label={"Expiration Date"}
                        {...register("coClient.DRIVER_LICENSE_EXP")}
                        error={errors.coClient?.DRIVER_LICENSE_EXP}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 lg:mt-0">
                <div>
                  <InputLabel value="Employment Status" />
                  <Controller
                    name="coClient.EMPLOYMENT_STATUS"
                    control={control}
                    render={({ field }) => (
                      <EmploymentStatusSelect
                        error={errors.coClient?.EMPLOYMENT_STATUS}
                        {...field}
                        onChange={(value: any) => field.onChange(value)}
                      />
                    )}
                  />
                </div>
                <div>
                  <Input
                    label="Employer Name"
                    {...register("coClient.EMPLOYER_NAME")}
                    error={errors.coClient?.EMPLOYER_NAME}
                  />
                </div>
                <div>
                  <Input
                    label={"Occupation"}
                    {...register("coClient.OCCUPATION")}
                    error={errors.coClient?.OCCUPATION}
                  />
                </div>
                <div>
                  <Controller
                    name="coClient.HAS_FINRA_ASSOCIATION"
                    control={control}
                    render={({ field }) => (
                      <FinraAssociationSelect
                        label="FINRA Association"
                        {...field}
                        onChange={(value: boolean) => field.onChange(value)}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </Transition>
        </div>
        {serverError && (
          <span className="mb-4 mt-1 flex h-6 w-full flex-row items-center justify-center rounded bg-red-900 px-4 py-2 text-label-base font-normal text-white">
            Something went wrong, try again. If the error persist please contact
            your Advisor.
          </span>
        )}
        <div className="flex w-full flex-row justify-between space-x-2.5 pb-10 text-label-base">
          <div className="flex flex-row items-center">
            {currentPage === 2 && (
              <Button variant="tertiary" onClick={() => setCurrentPage(1)}>
                <div className="flex flex-row items-center gap-2">
                  <HiArrowLeft size={24} color={themeTextColors.primary} />
                  <span> Previous</span>
                </div>
              </Button>
            )}
          </div>

          <div className="flex flex-row items-center gap-2.5">
            {currentPage === 1 && (
              <Button
                variant="tertiary"
                onClick={onAddCoClient}
                disabled={!hasCoClient && isSubmitDisabled}
              >
                {hasCoClient ? "View" : "Add"} Co-Client
              </Button>
            )}
            {currentPage === 2 && (
              <Button
                variant="danger"
                onClick={() => {
                  setHasCoClient(false);
                  setCurrentPage(1);
                }}
              >
                Remove Co-Client
              </Button>
            )}
            <Button
              variant="primary"
              disabled={isSubmitDisabled || isSubmitting}
              type="submit"
            >
              {isSubmitting ? (
                <div className="flex flex-row items-center justify-center space-x-2">
                  <CgSpinner className="h-4 animate-spin" size={20} />
                  {hasCoClient && <span>Please wait...</span>}
                </div>
              ) : (
                "Submit"
              )}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default IntakeForm;
